// src/components/ProtectedRoute.jsx
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const ProtectedRoute = ({ children }) => {
  const { user, userData, loading, initialized } = useAuth();
  const location = useLocation();

  // Wait for auth to initialize before making any decisions
  // This prevents redirecting on refresh before Firebase Auth restores the session
  if (loading || !initialized) {
    return null; // AuthProvider shows the loading spinner, so we return null here
  }

  // Redirect to login if no user is authenticated
  // Save the attempted location so we can redirect back after login
  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Check email verification - redirect to login if not verified
  // But only if we're not already on the login page (to prevent redirect loops)
  if (!user.emailVerified && location.pathname !== '/') {
    console.warn('⚠️ User email not verified, redirecting to login');
    return <Navigate to="/" state={{ from: location, requireVerification: true }} replace />;
  }

  // Check if user document exists in Firestore
  // If userData is null but user exists, the document might still be loading or doesn't exist
  // Give it a moment, but if it's been initialized and still null, redirect to login
  // But only if we're not already on the login page
  // Also allow access if user is verified (they might be in the process of creating their profile)
  if (!userData && initialized && location.pathname !== '/' && user.emailVerified) {
    // User is verified but no userData - might be creating profile, give it a moment
    // Don't redirect immediately, let them access the route
    console.log('⚠️ User verified but userData not found yet, allowing access');
  } else if (!userData && initialized && location.pathname !== '/') {
    console.warn('⚠️ User document not found in Firestore, redirecting to login');
    return <Navigate to="/" state={{ from: location, missingUserData: true }} replace />;
  }

  // If PM has been removed from their company, send them to a dedicated screen
  if (
    userData &&
    userData.role === 'pm' &&
    !userData.companyId &&
    userData.removedFromCompanyAt &&
    location.pathname !== '/removed'
  ) {
    return <Navigate to="/removed" replace />;
  }

  // If user is not verified but we're on login page, allow them to stay
  // The Login component will handle showing the verification modal
  if (!user.emailVerified && location.pathname === '/') {
    return null; // Let Login component handle it
  }

  // Render the protected content if the user is authenticated and verified
  // Note: userData might be null temporarily during profile creation, so we allow access if verified
  return children;
};

export default ProtectedRoute;
